import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Chip, Button, CircularProgress,
    Alert, Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, Tooltip, alpha, Checkbox,
} from '@mui/material';
import { Edit, Delete, Send, Add, Download } from '@mui/icons-material';
import axios from 'axios';

const statusConfig = {
    brouillon: { label: 'Brouillon', color: 'warning' },
    pret: { label: 'Pret', color: 'info' },
    soumis: { label: 'Soumis', color: 'success' },
    erreur: { label: 'Erreur', color: 'error' },
};

const CertificationDraftList = ({ onNewCertificate, onEditDraft, palette }) => {
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sending, setSending] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [sendDialog, setSendDialog] = useState(null);
    const [sendResult, setSendResult] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [exporting, setExporting] = useState(false);

    const fetchDrafts = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axios.get('/api/certif/drafts');
            setDrafts(response.data?.data || []);
        } catch (err) {
            setError('Erreur lors du chargement des certificats.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

    const handleSendToTej = async (draft) => {
        setSendDialog(null);
        setSending(draft._id);
        setSendResult(null);
        try {
            const res = await axios.post(`/api/certif/drafts/${draft._id}/send`);
            const nbCerts = draft.certificates?.length || 1;
            const hasErrors = res.data?.data?.some(r => !r.success);
            setSendResult({
                success: !hasErrors,
                message: hasErrors
                    ? 'Certains certificats ont echoue. Verifiez les details.'
                    : `${nbCerts} certificat(s) envoye(s) avec succes sur TEJ.`,
            });
            fetchDrafts();
        } catch (err) {
            setSendResult({ success: false, message: err?.response?.data?.message || "Erreur lors de l'envoi a TEJ." });
        } finally {
            setSending(null);
        }
    };

    const handleDelete = async () => {
        if (!deleteDialog) return;
        setDeleting(true);
        try {
            await axios.delete(`/api/certif/drafts/${deleteDialog._id}`);
            setDeleteDialog(null);
            fetchDrafts();
        } catch (err) {
            setError('Erreur lors de la suppression.');
        } finally {
            setDeleting(false);
        }
    };

    const editableDrafts = drafts.filter(d => d.status === 'brouillon' || d.status === 'pret' || d.status === 'erreur');

    const handleToggleSelect = (draftId) => {
        setSelectedIds(prev => prev.includes(draftId) ? prev.filter(id => id !== draftId) : [...prev, draftId]);
    };

    const handleToggleAll = () => {
        setSelectedIds(selectedIds.length === editableDrafts.length ? [] : editableDrafts.map(d => d._id));
    };

    const selectedFromDifferentClients = () => {
        const clients = new Set(
            drafts.filter(d => selectedIds.includes(d._id))
                .map(d => (typeof d.clientId === 'object' ? d.clientId._id : d.clientId))
        );
        return clients.size > 1;
    };

    const handleExportXml = async (ids) => {
        const idsToExport = ids || selectedIds;
        if (idsToExport.length === 0) return;
        setExporting(true);
        try {
            const response = await axios.post('/api/certif/drafts/export-xml', { draftIds: idsToExport }, { responseType: 'blob' });
            const disposition = response.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?(.+?)"?$/);
            const filename = match ? match[1] : 'certificats.xml';
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            const text = err?.response?.data instanceof Blob
                ? JSON.parse(await err.response.data.text()).message
                : err?.response?.data?.message || "Erreur lors de l'export XML.";
            setSendResult({ success: false, message: text });
        } finally {
            setExporting(false);
        }
    };

    const getClientName = (draft) => {
        const c = draft.clientId;
        if (!c) return '-';
        return c.raisonSociale || `${c.prenom || ''} ${c.nom || ''}`.trim() || '-';
    };

    const getBeneficiariesLabel = (draft) => {
        const certs = draft.certificates || [];
        if (certs.length === 0) return '-';
        const first = certs[0]?.beneficiary?.name || certs[0]?.beneficiary?.identifier || '-';
        if (certs.length === 1) return first;
        return `${first} (+${certs.length - 1})`;
    };

    const getTotalHT = (draft) => {
        const certs = draft.certificates || [];
        const total = certs.reduce((sum, c) => sum + (c.amounts?.montantHT || 0), 0);
        return total > 0 ? `${total.toFixed(3)} TND` : '-';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    if (loading) {
        return <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: palette.textPrimary }}>Mes certificats</Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {selectedIds.length > 0 && (
                        <Button
                            variant="outlined"
                            startIcon={exporting ? <CircularProgress size={18} /> : <Download />}
                            onClick={() => handleExportXml()}
                            disabled={exporting || selectedFromDifferentClients()}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                        >
                            {selectedFromDifferentClients() ? 'Clients differents' : `Telecharger XML (${selectedIds.length})`}
                        </Button>
                    )}
                    <Button variant="contained" startIcon={<Add />} onClick={onNewCertificate}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.teal2} 100%)`, '&:hover': { background: `linear-gradient(135deg, ${palette.teal2} 0%, ${palette.green} 100%)` } }}>
                        Nouveaux certificats
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {drafts.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: `1px dashed ${alpha(palette.navy, 0.2)}` }}>
                    <Typography sx={{ color: palette.textSecondary }}>Aucun certificat pour le moment.</Typography>
                    <Button variant="outlined" onClick={onNewCertificate} sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                        Creer mes premiers certificats
                    </Button>
                </Paper>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(palette.navy, 0.08)}` }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: alpha(palette.navy, 0.04) }}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selectedIds.length > 0 && selectedIds.length < editableDrafts.length}
                                        checked={editableDrafts.length > 0 && selectedIds.length === editableDrafts.length}
                                        onChange={handleToggleAll}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Beneficiaire(s)</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Client</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Nb certifs</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Total HT</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Modifie le</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {drafts.map((draft) => {
                                const st = statusConfig[draft.status] || statusConfig.brouillon;
                                const isEditable = draft.status === 'brouillon' || draft.status === 'pret' || draft.status === 'erreur';
                                const canSend = draft.status === 'pret' || draft.status === 'erreur';
                                const nbCerts = draft.certificates?.length || 0;

                                return (
                                    <TableRow key={draft._id} hover>
                                        <TableCell padding="checkbox">
                                            {isEditable ? (
                                                <Checkbox size="small" checked={selectedIds.includes(draft._id)} onChange={() => handleToggleSelect(draft._id)} />
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{getBeneficiariesLabel(draft)}</Typography>
                                        </TableCell>
                                        <TableCell>{getClientName(draft)}</TableCell>
                                        <TableCell>
                                            <Chip label={nbCerts} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>{getTotalHT(draft)}</TableCell>
                                        <TableCell>
                                            <Chip label={st.label} color={st.color} size="small" />
                                        </TableCell>
                                        <TableCell>{formatDate(draft.updatedAt)}</TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                {isEditable && (
                                                    <>
                                                        <Tooltip title="Modifier">
                                                            <IconButton size="small" onClick={() => onEditDraft(draft)}>
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Telecharger XML">
                                                            <IconButton size="small" onClick={() => handleExportXml([draft._id])} disabled={exporting}>
                                                                <Download fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {canSend && (
                                                            <Tooltip title="Envoyer a TEJ">
                                                                <IconButton size="small" color="primary" onClick={() => setSendDialog(draft)} disabled={sending === draft._id}>
                                                                    {sending === draft._id ? <CircularProgress size={18} /> : <Send fontSize="small" />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="Supprimer">
                                                            <IconButton size="small" color="error" onClick={() => setDeleteDialog(draft)}>
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {sendResult && (
                <Alert severity={sendResult.success ? 'success' : 'error'} sx={{ mt: 2, borderRadius: 2 }} onClose={() => setSendResult(null)}>
                    {sendResult.message}
                </Alert>
            )}

            <Dialog open={!!sendDialog} onClose={() => setSendDialog(null)}>
                <DialogTitle sx={{ fontWeight: 700 }}>Confirmer l'envoi a TEJ</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {sendDialog?.certificates?.length || 0} certificat(s) du groupe seront soumis sur la plateforme TEJ. Cette action est irreversible.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSendDialog(null)}>Annuler</Button>
                    <Button onClick={() => handleSendToTej(sendDialog)} variant="contained" color="primary">Confirmer l'envoi</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
                <DialogTitle>Supprimer le groupe ?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Le groupe ({deleteDialog?.certificates?.length || 0} certificat(s)) sera definitivement supprime.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog(null)} disabled={deleting}>Annuler</Button>
                    <Button onClick={handleDelete} color="error" disabled={deleting}>
                        {deleting ? <CircularProgress size={20} /> : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CertificationDraftList;
